import { ApolloQueryResult } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { RadioGroup } from '@atlaskit/radio';
import Textfield from '@atlaskit/textfield';
import get from 'lodash/get';
import set from 'lodash/set';
import * as React from 'react';
import { Currency, PaymentOnTerms } from '@css-xp/graphtypes';
import { useSidebar } from '../../../../../base/hooks/src/use-sidebar';
import { CreatePaymentMethodRequest } from '@atlassian/cssxp-types-billing';
import { ErrorMessageOnInfoPanel } from '../../../../../base/error-page/src/error-message-on-info-panel';
import { useFormSubmit } from '../../../../../base/hooks/src/use-form-submit';
import { useNotification, successNotificationBuilder, InfoPanel, NotificationTypes } from '@css-xp/base';
import { createPaymentMethod } from '../../../../../../utils/proxies/src/transaction-account/transaction-account';

interface FormData {
  paymentMethod?: PaymentOnTerms | null;
}

export enum InVoiceDueDays {
  DEFAULT = 30,
}

interface Props {
  transactionAccountId: string;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

export const AddPaymentOnTermsMethod = ({ transactionAccountId, refetch }: Props) => {
  const { closeSidebar } = useSidebar();
  const [showInvoicDueDays, setShowInvoicDueDays] = React.useState(false);

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<CreatePaymentMethodRequest, any>({
    submitFormAction: createPaymentMethod,
    onComplete: () => {
      closeSidebar();
      if (refetch) {
        refetch();
      } else {
        window.location.reload();
      }
    },
  });

  const submitAddPaymentOnTermsDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });
    const paymentMethodRequest: CreatePaymentMethodRequest = {
      transactionAccountId,
      data: {
        type: get(transformedData, 'payment-method'),
        currency: Currency.USD,
        terms: {
          invoiceDueDays: get(transformedData, 'invoice-due-days'),
        },
        default: true,
      },
    };
    submitForm(paymentMethodRequest);
  };

  const getPaymentMethodOption = () => {
    return [
      {
        name: 'payment on terms',
        label: 'Payment on terms',
        value: 'DEFERRED',
        isDisabled: false,
      },
    ];
  };

  const paymentMethodOption: any[] = getPaymentMethodOption() || [];

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-billto-party-success')
      .withMessage('Successfully added payment method.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  return (
    <div style={{ maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={submitAddPaymentOnTermsDetails}>
        {({ formProps, submitting }) => (
          <form {...formProps}>
            <FormHeader title="Add payment on terms"></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorMessageOnInfoPanel
                      title="Unable to add the payment on terms due to the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '600px' }}>
                <Field name="payment-method" isRequired={true} id="payment-method">
                  {({ fieldProps }) => (
                    <RadioGroup
                      {...fieldProps}
                      options={paymentMethodOption}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const isPaymentMethodSelected = event.currentTarget.value === 'DEFERRED';
                        setShowInvoicDueDays(isPaymentMethodSelected);

                        fieldProps.onChange(event);
                      }}
                    />
                  )}
                </Field>
              </div>
            </FormSection>
            <FormSection>
              {showInvoicDueDays && (
                <Field
                  name="invoice-due-days"
                  label="Number of days"
                  defaultValue={InVoiceDueDays.DEFAULT}
                  isDisabled={true}
                >
                  {({ fieldProps }) => <Textfield {...fieldProps} name="invoice-due-days" />}
                </Field>
              )}
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
